import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogTitle,
	Label,
	SwitchField,
	Tab,
	Tabs,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { DialogActions } from "molecules/dialog/DialogActions";
import { Input } from "molecules/input/Input";
import { LoadingState } from "molecules/loadingState/LoadingState";
import { MissingDataState } from "molecules/missingDataState/MissingDataState";
import { useTranslation } from "react-i18next";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

import type {
	AccessGroup,
	AccessGroupObject,
} from "../../common/service/types/AccessGroup";
import {
	useAddAccessGroupMutation,
	useEditAccessGroupMutation,
	useGetAvailableObjectsForAccessGroupsQuery,
} from "../../common/redux/api/exopenApi";

import { useEffect, useState } from "react";

import { ColorPicker } from "./ColorPicker";

import type { Permission } from "src/common/context/useUser";
import { useLoadedLegalEntities } from "src/routes/routeDataHooks";

const permissionSet = new Set<Permission>([
	"DataSieUpload",
	"DataLayer",
	"ExopenAi",
	"ExopenForExcel",
]);

const filterPermissions = (accessGroupObject: AccessGroupObject) => {
	return permissionSet.has(accessGroupObject.objectId as Permission);
};

interface Inputs {
	name: string;
	description: string;
	colorCode: string;
	permissions: string[];
	legalEntities: string[];
}

type AddAccessGroupModalProps = {
	modalOpen: boolean;
	handleClose: () => void;
	companyId: string;
	accessGroup?: AccessGroup | undefined;
};

export const AddAccessGroupModal = ({
	modalOpen,
	handleClose,
	companyId,
	accessGroup,
}: AddAccessGroupModalProps) => {
	const { t } = useTranslation();

	const { data: accessGroupObjects, isLoading: isLoadingAccessGroupObjects } =
		useGetAvailableObjectsForAccessGroupsQuery(companyId);
	const legalEntities = useLoadedLegalEntities();
	const {
		register,
		handleSubmit,
		reset,
		control,
		formState: { isDirty, isSubmitting, errors },
		watch,
	} = useForm<Inputs>({
		defaultValues: {
			description: accessGroup?.description ?? "",
			name: accessGroup?.name ?? "",
			colorCode: accessGroup?.colorCode ?? "",
			legalEntities: [],
			permissions: [],
		},
	});

	useEffect(() => {
		if (accessGroup) {
			reset({
				colorCode: accessGroup.colorCode,
				permissions: accessGroup.accessGroupObjects
					.filter(filterPermissions)
					.map(({ objectId }) => objectId),
				name: accessGroup.name,
				description: accessGroup.description,
				legalEntities: accessGroup.legalEntities,
			});
		}
	}, [accessGroup, reset]);

	const permissionsNames: Partial<Record<Permission, string>> = {
		DataSieUpload: t("Can upload SIE-files"),
		DataLayer: t("Can access Intelligence Layer"),
		ExopenAi: t("Can access {{aiProductName}}"),
		ExopenForExcel: t("Can access Exopen for Excel"),
	};

	const [addAccessGroup] = useAddAccessGroupMutation();
	const [editAccessGroup] = useEditAccessGroupMutation();

	const [tab, setTab] = useState(0);

	const onClose = () => {
		reset({
			description: "",
			name: "",
			colorCode: "",
			legalEntities: [],
			permissions: [],
		});
		setTab(0);
		handleClose();
	};

	const permissionsObjects = accessGroupObjects?.filter(filterPermissions);

	const accessGroupObjectById: Record<string, AccessGroupObject> = {};
	for (const accessGroupObject of accessGroupObjects ?? []) {
		accessGroupObjectById[accessGroupObject.objectId] = accessGroupObject;
	}

	const onSubmit: SubmitHandler<Inputs> = async (data) => {
		const accessGroupObjects = [
			...data.permissions.map((objectId) => {
				return {
					objectType: "Admin",
					objectId,
				};
			}),
		];
		const result = await (accessGroup
			? editAccessGroup({
					accessGroupObjects,
					colorCode: data.colorCode,
					companyId,
					description: data.description,
					legalEntities: data.legalEntities,
					name: data.name,
					groupId: accessGroup.groupId,
				})
			: addAccessGroup({
					accessGroupObjects,
					colorCode: data.colorCode,
					companyId,
					description: data.description,
					legalEntities: data.legalEntities,
					name: data.name,
				}));

		if ("error" in result) {
			showErrorNotification({
				message: accessGroup
					? t("The changes couldn't be saved")
					: t("Failed to add the group"),
			});
		} else {
			showSuccessNotification({
				message: accessGroup
					? t("The changes are saved")
					: t("The group was created"),
			});
			onClose();
		}
	};

	const [legalEntitiesFormValue, permissions] = watch([
		"legalEntities",
		"permissions",
	]);

	return (
		<Dialog open={modalOpen} onClose={handleClose}>
			<DialogContent size="xl">
				<DialogTitle>
					{accessGroup ? t("Edit group") : t("Create new group")}
				</DialogTitle>

				<DialogBody>
					{isLoadingAccessGroupObjects ? (
						<LoadingState />
					) : !accessGroupObjects ? (
						<MissingDataState />
					) : (
						<form id="add-edit-group-form" onSubmit={handleSubmit(onSubmit)}>
							<div className="flex flex-col gap-6 md:flex-row">
								<div className="min-w-0 shrink-0 flex-grow basis-0 space-y-4">
									<div>
										<Label htmlFor="access-group-name-input">{t("Name")}</Label>
										<Input
											{...register("name", {
												required: t("This field is required"),
											})}
											id="access-group-name-input"
											aria-invalid={errors.name !== undefined}
											hint={errors.name?.message}
										/>
									</div>

									<div>
										<Label htmlFor="access-group-description-input">
											{t("Description")}
										</Label>
										<Input
											{...register("description")}
											id="access-group-description-input"
										/>
									</div>
									<div>
										<Label>{t("Choose colour for group")}</Label>
										<Controller
											control={control}
											name="colorCode"
											render={({ field }) => {
												return (
													<ColorPicker
														value={field.value}
														onChange={field.onChange}
													/>
												);
											}}
										/>
									</div>
								</div>
								<div className="min-w-0 shrink-0 flex-grow basis-0">
									<Tabs
										selectedIndex={tab}
										onSelectTab={(index) => {
											setTab(index);
										}}
										className="mb-4"
									>
										<Tab count={legalEntitiesFormValue.length}>
											{t("Legal entities")}
										</Tab>
										<Tab count={permissions.length}>{t("Features")}</Tab>
									</Tabs>
									<Controller
										control={control}
										name="legalEntities"
										render={({ field: { value, onChange, ...field } }) => {
											if (tab !== 0) {
												return <>{null}</>;
											}
											return (
												<>
													<h6 className="mb-4">
														{t("Add legal entities to the group")}
													</h6>
													{legalEntities.length === 0 ? (
														t("No legal entities available")
													) : (
														<div className="max-h-[300px] space-y-2 overflow-y-auto">
															{legalEntities.map((legalEntity) => {
																return (
																	<div key={legalEntity.id}>
																		<SwitchField
																			{...field}
																			label={legalEntity.name}
																			checked={value.includes(
																				legalEntity.legalEntityMasterKey,
																			)}
																			onChange={(event) => {
																				if (event.target.checked) {
																					onChange([
																						...value,
																						legalEntity.legalEntityMasterKey,
																					]);
																				} else {
																					onChange(
																						value.filter((legalEntityId) => {
																							return (
																								legalEntityId !==
																								legalEntity.legalEntityMasterKey
																							);
																						}),
																					);
																				}
																			}}
																		/>
																	</div>
																);
															})}
														</div>
													)}
												</>
											);
										}}
									/>
									<Controller
										control={control}
										name="permissions"
										render={({ field: { value, onChange, ...field } }) => {
											if (tab !== 1) {
												return <>{null}</>;
											}
											return (
												<div className="max-h-[300px] space-y-2 overflow-y-auto">
													{permissionsObjects?.map((permission) => {
														return (
															<div key={permission.objectId}>
																<SwitchField
																	{...field}
																	label={
																		permissionsNames[
																			permission.name as Permission
																		] ?? ""
																	}
																	checked={value.includes(permission.objectId)}
																	onChange={(event) => {
																		if (event.target.checked) {
																			onChange([...value, permission.objectId]);
																		} else {
																			onChange(
																				value.filter((martId) => {
																					return martId !== permission.objectId;
																				}),
																			);
																		}
																	}}
																/>
																{permission.objectId === "ExopenAi" &&
																	value.includes(permission.objectId) && (
																		<p className="mt-1 text-sm text-gray-500">
																			{t(
																				"On its own this right does nothing — {{aiProductName}} answers for every company in the domain, so it also requires the CompanyAdmin group.",
																			)}
																		</p>
																	)}
															</div>
														);
													})}
												</div>
											);
										}}
									/>
								</div>
							</div>
						</form>
					)}
				</DialogBody>
				<DialogActions withCancelButton>
					<Button
						isLoading={isSubmitting}
						form="add-edit-group-form"
						type="submit"
						disabled={isSubmitting || !isDirty}
					>
						{accessGroup ? t("Save changes") : t("Create group")}
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
};
